import { useCallback, useEffect, useState } from "react";

export type HealthStatus = "healthy" | "unhealthy" | "checking";

interface HealthResponse {
  status: string;
  timestamp: string;
  launchedAt: string;
  lastLaunchedAt: string | null;
}

interface SystemState {
  status: HealthStatus;
  lastChecked: Date | null;
  launchedAt: Date | null;
  lastLaunchedAt: Date | null;
}

export function useSystemStatus() {
  const [state, setState] = useState<SystemState>({
    status: "checking",
    lastChecked: null,
    launchedAt: null,
    lastLaunchedAt: null,
  });

  const checkHealth = useCallback(async (): Promise<void> => {
    try {
      setState((prev) => ({ ...prev, status: "checking" }));
      const response = await fetch("/api/health");

      if (response.ok) {
        const data = (await response.json()) as HealthResponse;
        setState({
          status: data.status === "ok" ? "healthy" : "unhealthy",
          lastChecked: new Date(),
          launchedAt: new Date(data.launchedAt),
          lastLaunchedAt: data.lastLaunchedAt ? new Date(data.lastLaunchedAt) : null,
        });
      } else {
        setState((prev) => ({
          ...prev,
          status: "unhealthy",
          lastChecked: new Date(),
        }));
      }
    } catch {
      setState((prev) => ({
        ...prev,
        status: "unhealthy",
        lastChecked: new Date(),
      }));
    }
  }, []);

  useEffect(() => {
    // Initial check
    void checkHealth();

    // Check every 30 seconds
    const interval = setInterval(() => {
      void checkHealth();
    }, 30000);

    return () => clearInterval(interval);
  }, [checkHealth]);

  return {
    ...state,
    checkHealth,
  };
}
